import React, { useEffect, useRef, useState } from 'react';
import P from 'prop-types';
import { GlobalContext } from '../../contexts/HomeContext';
import { ResultadoBusca } from '../ResultadoBusca';
import { trackGAEvent, trackMetaPixelEvent } from '../../utils/tracking';
import './styles.css';

export const BuscaDropdown = ({ open, onClose, anchorRef }) => {
  const { dadosApp } = React.useContext(GlobalContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResultsCount, setSearchResultsCount] = useState(0);
  const [activeCategory, setActiveCategory] = useState('');
  const [dropdownTop, setDropdownTop] = useState(0);
  const [dropdownRight, setDropdownRight] = useState(0);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const lastTrackedRef = useRef('');

  const getCategoriaId = (item) => {
    const categoria = item.categoria_ecommerce === '' ? item.subgrupo : item.categoria_ecommerce;
    return categoria.toUpperCase().trim();
  };

  const measureDropdown = () => {
    const el = anchorRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setDropdownTop(rect.top + rect.height);
    }
    const dd = dropdownRef.current;
    if (dd) setDropdownRight(window.innerWidth - dd.getBoundingClientRect().right);
  };

  useEffect(() => {
    if (!open) return;
    measureDropdown();
    window.addEventListener('resize', measureDropdown);
    return () => window.removeEventListener('resize', measureDropdown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = scrollbarWidth + 'px';
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    lastTrackedRef.current = '';
    setSearchTerm('');
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const updateActiveCategory = () => {
      let scrollDistance = document.body.scrollTop || document.documentElement.scrollTop;
      scrollDistance = scrollDistance - 250;
      const sections = document.querySelectorAll('.page-section');
      let activeCategoryId = '';
      sections.forEach(function (i) {
        if (i.offsetTop <= scrollDistance) {
          activeCategoryId = i.id.trim();
        }
      });
      if (!activeCategoryId && sections.length > 0) {
        activeCategoryId = sections[0].id.trim();
      }
      setActiveCategory(activeCategoryId || '');
    };
    updateActiveCategory();
    window.addEventListener('scroll', updateActiveCategory);
    return () => window.removeEventListener('scroll', updateActiveCategory);
  }, [open]);

  const trackSearch = (termo, count) => {
    trackMetaPixelEvent('Search', {
      search_string: termo,
      content_type: 'product',
      value: count,
    });

    trackGAEvent('Ecommerce', 'Search', termo, count);
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const termo = searchTerm.trim();
    if (termo.length < 2) return;

    debounceRef.current = setTimeout(() => {
      if (lastTrackedRef.current === termo) return;
      lastTrackedRef.current = termo;
      trackSearch(termo, searchResultsCount);
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, searchResultsCount]);

  const handleDropdownSelect = (item) => {
    const categoria = getCategoriaId(item);
    onClose();
    setTimeout(() => {
      const element = document.getElementById(categoria);
      if (!element) return;
      const offsetPosition = element.getBoundingClientRect().top + window.scrollY - 150;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }, 280);
  };

  return (
    <>
      <div className={`busca__overlay${open ? ' open' : ''}`} onClick={onClose} />
      <div
        className={`container busca__dropdown${open ? ' open' : ''}`}
        ref={dropdownRef}
        style={{
          '--dropdown-top': `${dropdownTop}px`,
          '--dropdown-right': `${dropdownRight}px`,
        }}
      >
        <div className="busca__dropdown-header">
          <i className="material-icons busca__dropdown-search-icon">search</i>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Buscar produto..."
            className="busca__dropdown-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button
            type="button"
            className={`busca__dropdown-close${open ? '' : ' busca__hidden'}`}
            style={{ right: 14 }}
            onClick={onClose}
            aria-label="Fechar busca"
          >
            <i className="material-icons">close</i>
          </button>
        </div>
        <div className="busca__dropdown-body">
          {searchTerm.trim().length > 0 ? (
            <ResultadoBusca searchTerm={searchTerm} onAdded={onClose} onCountChange={setSearchResultsCount} />
          ) : (
            dadosApp.Cardapio.Categorias.length > 0 &&
            dadosApp.Cardapio.Categorias.map((item) => {
              const categoriaName = item.categoria_ecommerce === '' ? item.subgrupo : item.categoria_ecommerce;
              const isActive = activeCategory && categoriaName.toUpperCase().trim() === activeCategory;
              return (
                <button
                  key={String(item.id)}
                  type="button"
                  className={`busca__dropdown-item${isActive ? ' busca__item-active' : ''}`}
                  onClick={() => handleDropdownSelect(item)}
                >
                  {categoriaName.toLowerCase()}
                </button>
              );
            })
          )}
        </div>
      </div>
    </>
  );
};

BuscaDropdown.propTypes = {
  open: P.bool,
  onClose: P.func,
  anchorRef: P.object,
};
